import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  getTemplateForDevice,
  getAllTemplatesTable,
  getAllTemplatesListForm,
  getFullTemplate,
  updateTemplate,
  createTemplate,
} from '@app/api/template.api'
import type { AllTemplatesListFormResponse, DeviceTemplate, GetTemplateForDevicePayload } from '@app/api/template.api'

import { getErrorMessage } from '@/utils/utils'
import { Status } from '@/types/general-types'

type DeviceState = {
  templateDetailsStatus: Status
  allTemplatesTableStatus: Status
  templatesFormDataStatus: Status
  saveTemplateStatus: Status
  allTemplatesData: { [key: string]: any }
}

export type TableTemplate = {
  id: string
  name: string
  updatedAt: Date
  createdAt: Date
  devices: { id: string; name: string }[]
  language: string
}

const initialState: DeviceState = {
  allTemplatesData: {},
  templateDetailsStatus: 'idle',
  templatesFormDataStatus: 'idle',
  allTemplatesTableStatus: 'idle',
  saveTemplateStatus: 'idle',
}

export const getTemplateForDeviceData = createAsyncThunk<DeviceTemplate, GetTemplateForDevicePayload>(
  'templates/getTemplateForDevice',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await getTemplateForDevice(payload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllTemplatesListFormData = createAsyncThunk<AllTemplatesListFormResponse[]>(
  'templates/getAllDeviceTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllTemplatesListForm()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getAllTemplatesTableData = createAsyncThunk<TableTemplate[]>(
  'templates/getAllTemplatesTable',
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllTemplatesTable()

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateTemplateData = createAsyncThunk<void, any>(
  'templates/updateTemplateData',
  async (updateTemplatePayload, { rejectWithValue }) => {
    try {
      const res = await updateTemplate(updateTemplatePayload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const createTemplateData = createAsyncThunk<string, any>(
  'templates/createTemplateData',
  async (createTemplatePayload, { rejectWithValue }) => {
    try {
      const res = await createTemplate(createTemplatePayload)

      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const getFullTemplateData = createAsyncThunk<DeviceTemplate, string>(
  'templates/getFullTemplateData',
  async (id, { rejectWithValue }) => {
    try {
      const res = await getFullTemplate(id)
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const templateSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllTemplatesTableData.pending, (state) => {
        state.allTemplatesTableStatus = 'loading'
      })
      .addCase(getTemplateForDeviceData.pending, (state) => {
        state.templateDetailsStatus = 'loading'
      })
      .addCase(updateTemplateData.pending, (state) => {
        state.saveTemplateStatus = 'loading'
      })
      .addCase(createTemplateData.pending, (state) => {
        state.saveTemplateStatus = 'loading'
      })
      .addCase(getFullTemplateData.pending, (state) => {
        state.templateDetailsStatus = 'loading'
      })
      .addCase(getAllTemplatesListFormData.pending, (state) => {
        state.templatesFormDataStatus = 'loading'
      })

      .addCase(getTemplateForDeviceData.rejected, (state) => {
        state.templateDetailsStatus = 'failed'
      })
      .addCase(getAllTemplatesTableData.rejected, (state) => {
        state.allTemplatesTableStatus = 'failed'
      })
      .addCase(updateTemplateData.rejected, (state) => {
        state.saveTemplateStatus = 'failed'
      })
      .addCase(createTemplateData.rejected, (state) => {
        state.saveTemplateStatus = 'failed'
      })
      .addCase(getAllTemplatesListFormData.rejected, (state) => {
        state.templatesFormDataStatus = 'failed'
      })
      .addCase(getFullTemplateData.rejected, (state) => {
        state.templateDetailsStatus = 'failed'
      })

      .addCase(getTemplateForDeviceData.fulfilled, (state, action) => {
        const templateId = action.payload.id
        const exsisting = state.allTemplatesData[templateId] || {}

        state.allTemplatesData[templateId] = {
          ...exsisting,
          ...action.payload,
        }

        state.templateDetailsStatus = 'succeeded'
      })
      .addCase(getAllTemplatesTableData.fulfilled, (state, action) => {
        action.payload.forEach((template) => {
          const existing = state.allTemplatesData[template.id] || {}

          state.allTemplatesData[template.id] = {
            ...existing,
            ...template,
          }
        })

        state.allTemplatesTableStatus = 'succeeded'
      })
      .addCase(updateTemplateData.fulfilled, (state, action) => {
        const { id, ...data } = action.meta.arg
        const existing = state.allTemplatesData[id]

        state.allTemplatesData[id] = {
          ...existing,
          ...data,
        }

        state.saveTemplateStatus = 'succeeded'
      })
      .addCase(createTemplateData.fulfilled, (state, action) => {
        const id = action.payload
        const data = action.meta.arg
        const newTemplate = {
          id,
          ...data,
        }

        state.allTemplatesData = {
          [id]: newTemplate,
          ...state.allTemplatesData,
        }

        state.saveTemplateStatus = 'succeeded'
      })
      .addCase(getAllTemplatesListFormData.fulfilled, (state, action: PayloadAction<any[]>) => {
        action.payload.forEach((template) => {
          const existing = state.allTemplatesData[template.id] || {}

          state.allTemplatesData[template.id] = {
            ...existing,
            ...template,
          }
        })

        state.templatesFormDataStatus = 'succeeded'
      })
      .addCase(getFullTemplateData.fulfilled, (state, action) => {
        const templateId = action.meta.arg
        const exsisting = state.allTemplatesData[templateId]

        state.allTemplatesData[templateId] = {
          ...exsisting,
          ...action.payload,
        }

        state.templateDetailsStatus = 'succeeded'
      })
  },
})

export default templateSlice.reducer
